import * as React from "react";

// Appearance and behavior preferences (Account › Appearance, ui-spec IMP-1, IMP-9, §4.4, §3.3). The theme lives in
// a cookie shared across the console, site and docs subdomains so the first paint is right everywhere; the rest is
// browser-local. Every storage access is wrapped, because storage can be disabled.

export type Theme = "system" | "light" | "dark";

export interface Prefs {
  theme: Theme;
  timeZone: string;
  /** Extrapolate running costs once a second between watch events (on by default). */
  tickCosts: boolean;
  /** Omit -p/--org in copied commands (off by default). */
  omitScope: boolean;
}

const DEFAULTS: Prefs = {
  // Dark first (the dashboard default); "system" follows prefers-color-scheme.
  theme: "dark",
  timeZone: "UTC",
  tickCosts: true,
  omitScope: false,
};

const STORAGE_KEY = "nodus.prefs.v1"; // gitleaks:allow (a localStorage entry name, not a credential)
export const THEME_COOKIE = "nodus_theme";

function readCookie(name: string): string | undefined {
  try {
    return document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${name}=`))
      ?.slice(name.length + 1);
  } catch {
    return undefined;
  }
}

function writeThemeCookie(theme: Theme, cookieDomain?: string) {
  try {
    const domain = cookieDomain ? `; domain=${cookieDomain}` : "";
    document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000; samesite=lax${domain}`;
  } catch {
    // Cookies disabled: the theme still applies for this page view.
  }
}

function load(): Prefs {
  let stored: Partial<Prefs> & { density?: unknown; shortcuts?: unknown } = {};
  try {
    stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}") ?? {};
    // Retired preferences never override the fixed comfortable layout.
    if (Object.hasOwn(stored, "density") || Object.hasOwn(stored, "shortcuts")) {
      delete stored.density;
      delete stored.shortcuts;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    stored = {};
  }
  const cookieTheme = readCookie(THEME_COOKIE);
  const theme = cookieTheme === "light" || cookieTheme === "dark" || cookieTheme === "system" ? cookieTheme : undefined;
  return { ...DEFAULTS, ...stored, ...(theme ? { theme } : {}) };
}

/** Applies theme and clears legacy compact density; absent data-theme follows the system theme. */
export function applyAppearance(p: Pick<Prefs, "theme">) {
  const root = document.documentElement;
  if (p.theme === "system") delete root.dataset.theme;
  else root.dataset.theme = p.theme;
  delete root.dataset.density;
}

interface PrefsContextValue extends Prefs {
  set: (patch: Partial<Prefs>) => void;
}

const PrefsContext = React.createContext<PrefsContextValue>({ ...DEFAULTS, set: () => {} });

export function PrefsProvider({ children, cookieDomain }: { children: React.ReactNode; cookieDomain?: string }) {
  const [prefs, setPrefs] = React.useState<Prefs>(load);

  React.useEffect(() => applyAppearance(prefs), [prefs]);

  const set = React.useCallback(
    (patch: Partial<Prefs>) => {
      setPrefs((prev) => {
        const next = { ...prev, ...patch };
        try {
          const { theme: _theme, ...rest } = next;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
        } catch {
          // Storage disabled: keep the preference for this session only.
        }
        if (patch.theme) writeThemeCookie(patch.theme, cookieDomain);
        return next;
      });
    },
    [cookieDomain],
  );

  const value = React.useMemo(() => ({ ...prefs, set }), [prefs, set]);
  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): PrefsContextValue {
  return React.useContext(PrefsContext);
}

/** A clock for relative times and cost tickers; one interval per hook, paused when the tab is hidden. */
export function useNow(intervalMs = 1000): Date {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") setNow(new Date());
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}
